import React, { useEffect, useMemo, useRef } from "react";
import heroSeed from "../../data/atlasSeed.json";
import { subscribe, prefersReducedMotion } from "./graphTicker";
import "../../styles/AtlasBackdrop.css";

// The living graph backdrop — canvas, not SVG.
//
// WHY CANVAS. The SVG version drew 61 nodes and read as a static diagram with a
// slight wobble. What makes a graph look *alive* is density and reorganisation,
// and SVG can't carry either: ~2,500 individually-transformed DOM elements at
// 60fps would melt a phone. Canvas draws the same 2,500 points as pixels in one
// pass and doesn't care.
//
// WHAT IT DOES. Every node in the seed becomes a small colony of particles.
// The colonies drift, and every few seconds the whole field eases into a
// different arrangement — rings, then type-clusters, then a torus, then a
// drifting cloud — so the structure visibly reorganises itself the way cells
// do. That morph is the thing the eye reads as "living" rather than "animated".
//
// (For reference: cosmograph.app achieves its version with a 26 MB pre-rendered
// mp4 and no canvas at all. This is ~25 kB of JSON and a few kB of code.)
//
// CONSTRAINTS THAT STILL APPLY.
//  - "/" is prerendered and hydrated, so first render must match the baked
//    HTML. A canvas element is empty in markup, which makes that trivial — but
//    it does mean the graph is no longer *in* the HTML. That's fine: the
//    backdrop is aria-hidden decoration and was never crawlable content.
//  - No network: the data is baked at build time.
//  - One shared rAF loop (graphTicker) drives every graph on the page, and an
//    IntersectionObserver unsubscribes whatever scrolls off screen.
//  - prefers-reduced-motion gets a single static frame, drawn once.

const VIEW_W = 1000;
const VIEW_H = 720;
const CX = VIEW_W / 2;
const CY = VIEW_H / 2;

export const TYPE_COLOR = {
  herb: [90, 169, 255],
  condition: [255, 154, 108],
  action: [79, 224, 173],
};
const DEFAULT_COLOR = [143, 163, 184];

// Deterministic pseudo-random — no Math.random anywhere, so the layout is
// identical on every render and every machine.
const noise = (i, salt) => {
  const x = Math.sin((i + 1) * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

// --- the arrangements the field morphs between ----------------------------
// Each returns a position for node index i of n. Keep them all centred and
// roughly the same scale so the transition reads as reorganisation rather than
// as a jump cut.

const FORMS = [
  // 0. rings — the graph's own topology, by distance from the root
  (i, n, node, spread) => {
    const ring = Math.min(node.ring, 2);
    const r = [0, 150, 300][ring] * spread * (0.86 + noise(i, ring + 7) * 0.28);
    const a =
      (i / n) * Math.PI * 2 + ring * 2.39996 + (noise(i, ring) - 0.5) * 0.34;
    return [CX + Math.cos(a) * r * 1.32, CY + Math.sin(a) * r];
  },
  // 1. colonies — one blob per node type, like cells sorting themselves
  (i, n, node, spread) => {
    const idx = { herb: 0, condition: 1, action: 2 }[node.type] ?? 2;
    const bx = CX + [-1, 1, 0][idx] * 250 * spread;
    const by = CY + [-0.55, -0.2, 0.75][idx] * 180 * spread;
    const a = noise(i, 31) * Math.PI * 2;
    const r = Math.sqrt(noise(i, 37)) * 135 * spread;
    return [bx + Math.cos(a) * r * 1.15, by + Math.sin(a) * r];
  },
  // 2. torus — the shape a force layout settles into when it has room
  (i, n, node, spread) => {
    const t = (i / n) * Math.PI * 2;
    const wob = 1 + Math.sin(t * 5 + noise(i, 41) * 6) * 0.18;
    const r = 265 * spread * wob;
    return [CX + Math.cos(t) * r * 1.28, CY + Math.sin(t) * r * 0.82];
  },
  // 3. drift — an unstructured cloud, the "before" state
  (i, n, node, spread) => {
    const a = noise(i, 53) * Math.PI * 2;
    const r = Math.sqrt(noise(i, 59)) * 330 * spread;
    return [CX + Math.cos(a) * r * 1.3, CY + Math.sin(a) * r * 0.85];
  },
];

const HOLD = 6.5; // seconds a form is held
const MORPH = 3.2; // seconds to ease into the next one
const CYCLE = HOLD + MORPH;

// Smootherstep — slower at both ends than a cosine, which is what makes the
// reorganisation feel deliberate rather than mechanical.
const ease = (t) => t * t * t * (t * (t * 6 - 15) + 10);

const AtlasGraphBackdrop = ({
  seed = heroSeed,
  intensity = 1,
  spread = 1,
  // Particles per node. Cards are small and numerous; the hero can afford more.
  density = 96,
}) => {
  const model = useMemo(() => {
    const nodes = seed.nodes;
    const n = nodes.length;

    // Per-node target positions in every form.
    const forms = FORMS.map((fn) =>
      nodes.map((node, i) => fn(i, n, node, spread))
    );

    // Particles: each belongs to a node, sits at a fixed offset from it, and
    // breathes on its own period so no two ever move together.
    const particles = [];
    for (let i = 0; i < n; i += 1) {
      const node = nodes[i];
      const hub = 0.6 + Math.min(node.degree, 12) / 12; // busy nodes get bigger colonies
      const count = Math.max(8, Math.round(density * hub * 0.5));
      const color = TYPE_COLOR[node.type] || DEFAULT_COLOR;
      for (let k = 0; k < count; k += 1) {
        const a = noise(i * 97 + k, 3) * Math.PI * 2;
        const rad = Math.sqrt(noise(i * 97 + k, 11)) * (16 + hub * 26);
        particles.push({
          node: i,
          ox: Math.cos(a) * rad,
          oy: Math.sin(a) * rad,
          size: 0.8 + noise(i * 97 + k, 17) * 2.6,
          alpha: 0.34 + noise(i * 97 + k, 23) * 0.62,
          phase: noise(i * 97 + k, 29) * Math.PI * 2,
          speed: 0.12 + noise(i * 97 + k, 31) * 0.3,
          amp: 3 + noise(i * 97 + k, 37) * 9,
          color,
        });
      }
    }

    const edges = seed.edges
      .map((e) => ({
        a: seed.nodes.findIndex((x) => x.id === e.a),
        b: seed.nodes.findIndex((x) => x.id === e.b),
        w:
          e.ev === "clinical_plant"
            ? 1.5
            : e.ev === "clinical_extract"
            ? 1
            : 0.55,
      }))
      .filter((e) => e.a >= 0 && e.b >= 0);

    return { nodes, forms, particles, edges, n };
  }, [seed, spread]);

  const hostRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const { nodes, forms, edges, n } = model;
    // Phones do the same picture with fewer points: 2,700 arc fills per frame
    // is comfortable on a laptop and not on a mid-range handset.
    const lean = window.innerWidth < 820;
    const particles = lean
      ? model.particles.filter((_, i) => i % 5 !== 0 && i % 3 !== 0)
      : model.particles;
    const hubX = new Float32Array(n);
    const hubY = new Float32Array(n);

    // Cap the backing store: past 2x there is no visible gain and a lot of fill
    // cost on phones.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (t) => {
      const w = canvas.width;
      const h = canvas.height;
      // Cover-fit the 1000x720 design space into the element, like the SVG's
      // preserveAspectRatio="slice".
      const s = Math.max(w / VIEW_W, h / VIEW_H);
      const ox = (w - VIEW_W * s) / 2;
      const oy = (h - VIEW_H * s) / 2;

      // Which two forms are we between, and how far?
      //
      // t is clamped to a finite, non-negative number first. A non-finite t
      // makes `phase` NaN, which makes `from` NaN, which makes forms[from]
      // undefined — and the next line then throws on undefined[0]. That was
      // observed intermittently (not reproducibly) during hydration, and since
      // this whole layer is decoration it must never be able to throw: a graph
      // that stops moving is a cosmetic bug, an exception is a real one.
      const safeT = Number.isFinite(t) && t > 0 ? t : 0;
      const phase = (safeT % (CYCLE * FORMS.length)) / CYCLE;
      const from = Math.min(Math.floor(phase), FORMS.length - 1);
      const to = (from + 1) % FORMS.length;
      const local = (phase - Math.floor(phase)) * CYCLE;
      const k = local <= HOLD ? 0 : ease((local - HOLD) / MORPH);

      for (let i = 0; i < n; i += 1) {
        const a = forms[from][i];
        const b = forms[to][i];
        hubX[i] = a[0] + (b[0] - a[0]) * k;
        hubY[i] = a[1] + (b[1] - a[1]) * k;
      }

      ctx.clearRect(0, 0, w, h);

      // Edges first, underneath — faint, so the structure is felt not read.
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = `rgba(143, 217, 255, ${0.1 * intensity})`;
      for (let i = 0; i < edges.length; i += 1) {
        const e = edges[i];
        ctx.lineWidth = e.w * s * 0.7;
        ctx.beginPath();
        const x1 = ox + hubX[e.a] * s;
        const y1 = oy + hubY[e.a] * s;
        const x2 = ox + hubX[e.b] * s;
        const y2 = oy + hubY[e.b] * s;
        // Bowed, not straight — nothing in nature joins two points with a rule.
        const mx = (x1 + x2) / 2 - (y2 - y1) * 0.12;
        const my = (y1 + y2) / 2 + (x2 - x1) * 0.12;
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(mx, my, x2, y2);
        ctx.stroke();
      }

      ctx.globalCompositeOperation = "lighter";

      // Soft halo under each colony — three stacked low-alpha discs are a cheap
      // stand-in for a real bloom pass and cost nothing next to a blur filter.
      for (let i = 0; i < n; i += 1) {
        const node = nodes[i];
        const c = TYPE_COLOR[node.type] || DEFAULT_COLOR;
        const base = (10 + Math.min(node.degree, 12) * 2.4) * s;
        const x = ox + hubX[i] * s;
        const y = oy + hubY[i] * s;
        for (let ring = 3; ring >= 1; ring -= 1) {
          ctx.fillStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${
            0.035 * intensity
          })`;
          ctx.beginPath();
          ctx.arc(x, y, base * ring, 0, 6.283185);
          ctx.fill();
        }
      }

      // Particles, additively blended so overlaps bloom like real light.
      for (let p = 0; p < particles.length; p += 1) {
        const q = particles[p];
        const bx = hubX[q.node];
        const by = hubY[q.node];
        const dx = Math.sin(t * q.speed + q.phase) * q.amp;
        const dy = Math.cos(t * q.speed * 0.83 + q.phase * 1.4) * q.amp;
        const x = ox + (bx + q.ox + dx) * s;
        const y = oy + (by + q.oy + dy) * s;
        const c = q.color;
        ctx.fillStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${
          q.alpha * intensity
        })`;
        ctx.beginPath();
        ctx.arc(x, y, q.size * s, 0, 6.283185);
        ctx.fill();
      }

      // Hub cores — the actual graph nodes, brighter than their colony.
      for (let i = 0; i < n; i += 1) {
        const node = nodes[i];
        const c = TYPE_COLOR[node.type] || DEFAULT_COLOR;
        const r = (2.2 + Math.min(node.degree, 12) * 0.32) * s;
        ctx.fillStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${0.75 * intensity})`;
        ctx.beginPath();
        ctx.arc(ox + hubX[i] * s, oy + hubY[i] * s, r, 0, 6.283185);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";
    };

    if (prefersReducedMotion()) {
      draw(0); // one still frame, no loop
      return () => window.removeEventListener("resize", resize);
    }

    let unsubscribe = null;
    const start = () => {
      if (!unsubscribe) unsubscribe = subscribe(draw);
    };
    const stop = () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    };

    if (typeof IntersectionObserver !== "function" || !hostRef.current) {
      start();
      return () => {
        stop();
        window.removeEventListener("resize", resize);
      };
    }
    const observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? start() : stop()),
      { rootMargin: "120px" }
    );
    observer.observe(hostRef.current);
    return () => {
      observer.disconnect();
      stop();
      window.removeEventListener("resize", resize);
    };
  }, [model, intensity]);

  return (
    <div className="atlas-backdrop" aria-hidden="true" ref={hostRef}>
      <canvas ref={canvasRef} className="atlas-backdrop__canvas" />
    </div>
  );
};

export default AtlasGraphBackdrop;
